import React from 'react';
import { Link } from '@reach/router';
import styled from 'styled-components';

import Text from 'components/Text';
import Logo from 'components/Logo';
import AccountMenuDropdown from 'components/AccountMenuDropdown';
import IsAuthenticated from 'components/IsAuthenticated';
import HasRole from 'components/HasRole';

import { logoutUser } from 'reducers/auth/actions';

import { primaryText } from 'styles/colors';
import { ADMIN_AUTH_ROLES, USER_AUTH_ROLES } from 'utils/appConstants';
import { useDispatch } from 'react-redux';
import topologyIcon from 'assets/boxes-alt-light.svg';
import controlsIcon from 'assets/broadcast-tower-light.svg';
import devicesIcon from 'assets/boxes-alt-light.svg';

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #cfd1d3;
  height: 50px;
  width: 100%;
  background-color: white;
`;

const HeaderLeftLogo = styled.div`
  align-items: center;
  border-right: 1px solid #cfd1d3;
  display: flex;
  padding: 5px 15px;
  justify-content: center;
`;

const HeaderCenter = styled.div`
  display: flex;
  flex: 1;
  padding-top: 16px;
`;

const CenterLinks = styled.div`
  @media (min-width: 1024px) {
    transform: translateX(65%);
  }
`;

const HeaderRight = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  padding-right: 30px;
`;

const AdminLinks = styled.span`
  padding-right: 16px;
  height: 100%;
`;

const AllUsersLink = styled.span`
  padding-right: 16px;
  height: 100%;
`;

const HeaderIcon = styled.img`
  height: 20px;
  width: 20px;
  display: inline;
  vertical-align: middle;
  padding-left: 16px;
  box-sizing: content-box;
`;

const StyledLink = styled(Link)`
  display: inline-block;
  font-weight: normal;
  color: gray;
  height: 100%;
  margin: 0 8px;
  background: none;
  background-size: 0;
  background-position: 0;
  background-repeat: none;

  &[aria-current] {
    font-weight: bold;
    color: ${primaryText};
    background: linear-gradient(to left, #11b1d8, #0779af);
    background-size: 100% 4px;
    background-position: bottom, 0 calc(100% - 4px);
    background-repeat: no-repeat;
  }
`;

const NavLink = ({ children, styles, to, onClick, ...props }) => {
  return (
    <StyledLink to={to} onClick={onClick}>
      <Text
        style={{
          display: 'inline-block',
          fontSize: '14px',
          margin: '0px',
          verticalAlign: 'middle',
          ...styles
        }}
      >
        {children}
      </Text>
    </StyledLink>
  );
};

const AdminNavLinks = () => (
  <AdminLinks>
    <NavLink to="/configuration">Configuration</NavLink>
    <NavLink to="/activity-log">Activity Log</NavLink>
    <NavLink to="/manage-users">Manage Users</NavLink>
  </AdminLinks>
);

const AllUsersNavLink = () => (
  <AllUsersLink>
    <NavLink to="/configuration">Configuration</NavLink>
  </AllUsersLink>
);
const UserWriteProtectedNavLinks = HasRole(AllUsersNavLink, USER_AUTH_ROLES);

const AdminProtectedNavLinks = HasRole(AdminNavLinks, ADMIN_AUTH_ROLES);

const AuthenticatedNavLinks = ({ logout }) => (
  <React.Fragment>
    <HeaderCenter>
      <CenterLinks>
        <HeaderIcon src={topologyIcon} alt="Topology" />
        <NavLink to="/">Topology</NavLink>
        <HeaderIcon src={controlsIcon} alt="Controls" />
        <NavLink to="/controls">Controls</NavLink>
        <HeaderIcon src={devicesIcon} alt="Devices" />
        <NavLink to="/devices">Devices</NavLink>
      </CenterLinks>
    </HeaderCenter>

    <HeaderRight>
      <AdminProtectedNavLinks />
      <UserWriteProtectedNavLinks />
      <AccountMenuDropdown logout={logout} />
    </HeaderRight>
  </React.Fragment>
);

const UnauthenticatedNavLinks = () => (
  <React.Fragment>
    <HeaderRight>
      <NavLink to="/login">Sign In</NavLink>
    </HeaderRight>
  </React.Fragment>
);

const AuthProtectedNavLinks = IsAuthenticated(
  AuthenticatedNavLinks,
  UnauthenticatedNavLinks
);

const Navigation = () => {
  const dispatch = useDispatch();
  const logout = () => {
    dispatch(logoutUser());
  };

  return (
    <Nav>
      <HeaderLeftLogo>
        <Link to="/">
          <Logo height="30px" width="75px" />
        </Link>
      </HeaderLeftLogo>

      <AuthProtectedNavLinks logout={logout} />
    </Nav>
  );
};

export default Navigation;
